import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { organizationKeys, personKeys } from "@/lib/query";

export interface PersonLite {
  id: string;
  name: string;
  /** Free-text role title, shown on the Board roster. */
  title: string | null;
  /** full_time | part_time | contractor — drives the Board's FTE/contractor accent. */
  employmentType: string;
  avatarUrl: string | null;
}

export interface RoleTeam {
  id: string;
  departmentId: string;
  name: string;
  description: string | null;
  sortOrder: number;
  people: PersonLite[];
  /** The team lead, or null. Always one of `people`. */
  leadPersonId: string | null;
}

export interface Department {
  id: string;
  pillarId: string;
  name: string;
  description: string | null;
  sortOrder: number;
  roleTeams: RoleTeam[];
}

export interface Pillar {
  id: string;
  name: string;
  description: string | null;
  /** Named palette key or "#rrggbb" hex; null falls back to positional default. */
  color: string | null;
  /** The pillar head, or null. Grants aggregate access to the competency report (WI-284). */
  headPersonId: string | null;
  sortOrder: number;
  departments: Department[];
}

interface OrganizationTree {
  pillars: Pillar[];
}

export function useOrganization() {
  return useQuery({
    queryKey: organizationKeys.tree(),
    queryFn: async (): Promise<OrganizationTree> => {
      const res = await fetch("/api/organization");
      if (!res.ok) throw new Error("Failed to fetch organization");
      return res.json();
    },
  });
}

interface PillarInput {
  name: string;
  description?: string | null;
  color?: string | null;
  /** Only honoured on update. */
  headPersonId?: string | null;
}

interface DepartmentInput {
  pillarId: string;
  name: string;
  description?: string | null;
}

interface RoleTeamInput {
  departmentId: string;
  name: string;
  description?: string | null;
}

function makeMutation<TInput>(opts: {
  url: (input: TInput) => string;
  method: "POST" | "PUT" | "DELETE";
  body?: (input: TInput) => unknown;
  successMessage: string;
  errorMessage: string;
}) {
  return () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (input: TInput) => {
        const res = await fetch(opts.url(input), {
          method: opts.method,
          headers: opts.body ? { "Content-Type": "application/json" } : undefined,
          body: opts.body ? JSON.stringify(opts.body(input)) : undefined,
        });
        if (!res.ok) {
          // Prefer the server's message (e.g. a 409 "name already exists") over
          // the generic fallback so the toast tells the admin what to fix.
          const body = (await res.json().catch(() => null)) as { error?: string } | null;
          throw new Error(body?.error ?? opts.errorMessage);
        }
        if (res.status === 204) return null;
        return res.json();
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: organizationKeys.all });
        toast.success(opts.successMessage);
      },
      onError: (e: Error) => toast.error(e.message ?? opts.errorMessage),
    });
  };
}

/**
 * Set (`leadPersonId`) or clear (`null`) a role team's lead.
 *
 * Hand-written rather than via `makeMutation` because it must also drop the
 * person cache: the lead is stored on the team but badged on the person's
 * profile header, so invalidating only the org tree would leave a stale badge.
 */
export function useSetRoleTeamLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { roleTeamId: string; leadPersonId: string | null }) => {
      const res = await fetch(`/api/role-teams/${input.roleTeamId}/lead`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ leadPersonId: input.leadPersonId }),
      });
      if (!res.ok) {
        // The membership/active guards return messages written for this dialog.
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(body?.error ?? "Failed to set the team lead");
      }
      return res.json();
    },
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
      queryClient.invalidateQueries({ queryKey: personKeys.all });
      toast.success(input.leadPersonId ? "Team lead updated" : "Team lead removed");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export const useUpdatePillar = makeMutation<{ id: string; data: Partial<PillarInput> }>({
  url: ({ id }) => `/api/pillars/${id}`,
  method: "PUT",
  body: ({ data }) => data,
  successMessage: "Pillar updated",
  errorMessage: "Failed to update pillar",
});

export const useUpdateDepartment = makeMutation<{ id: string; data: Partial<DepartmentInput> }>({
  url: ({ id }) => `/api/departments/${id}`,
  method: "PUT",
  body: ({ data }) => data,
  successMessage: "Department updated",
  errorMessage: "Failed to update department",
});

export const useCreateRoleTeam = makeMutation<RoleTeamInput>({
  url: () => "/api/role-teams",
  method: "POST",
  body: (i) => i,
  successMessage: "Role team created",
  errorMessage: "Failed to create role team",
});

export const useUpdateRoleTeam = makeMutation<{ id: string; data: Partial<RoleTeamInput> }>({
  url: ({ id }) => `/api/role-teams/${id}`,
  method: "PUT",
  body: ({ data }) => data,
  successMessage: "Role team updated",
  errorMessage: "Failed to update role team",
});

export const useDeleteRoleTeam = makeMutation<{ id: string }>({
  url: ({ id }) => `/api/role-teams/${id}`,
  method: "DELETE",
  successMessage: "Role team deleted",
  errorMessage: "Failed to delete role team",
});

interface ReorderInput {
  level: "pillar" | "department" | "role_team";
  parentId: string | null;
  orderedIds: string[];
}

export const useReorderOrganization = makeMutation<ReorderInput>({
  url: () => "/api/organization/reorder",
  method: "POST",
  body: (i) => i,
  successMessage: "Order saved",
  errorMessage: "Failed to save order",
});

interface OrgSyncSummary {
  pillarsCreated: number;
  departmentsCreated: number;
  alreadyPresent: number;
  inactiveInNos: number;
  noPillarInNos: number;
  unknownPillar: number;
}

/**
 * Hand-written rather than via `makeMutation` because the success toast
 * depends on the response body (how many pillars/departments NOS added), not
 * a fixed string.
 */
export function useSyncOrgFromNos() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<OrgSyncSummary> => {
      const res = await fetch("/api/organization/sync", { method: "POST" });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        const message = (body as { error?: string } | null)?.error ?? "Sync with NOS failed";
        throw new Error(message);
      }
      return body as OrgSyncSummary;
    },
    onSuccess: (summary) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
      const created = summary.pillarsCreated + summary.departmentsCreated;
      toast.success(
        created === 0
          ? "Already up to date with NOS"
          : `Added ${summary.pillarsCreated} pillar${summary.pillarsCreated === 1 ? "" : "s"} and ` +
              `${summary.departmentsCreated} department${summary.departmentsCreated === 1 ? "" : "s"} from NOS`,
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
